/**
 * Orca Account Web - Authentication Module
 * Handles sign in, sign up, and social authentication
 */

import { createClient } from '@supabase/supabase-js';

// Configuration
const CONFIG = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'https://api.orcaslicer.com',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  redirectUrl: window.location.origin + '/dashboard.html'
};

// Initialize Supabase client
let supabase;
try {
  supabase = createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  });
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  showMessage('Failed to initialize authentication. Please check your configuration.', 'error');
}

// DOM Elements
const signinForm = document.getElementById('signin-form');
const signupForm = document.getElementById('signup-form');
const messageBox = document.getElementById('message');
const socialButtons = document.querySelectorAll('[data-provider]');
const forgotPasswordLink = document.getElementById('forgot-password');

// State
let currentForm = null;

/**
 * Initialize authentication
 */
async function initAuth() {
  if (!supabase) return;

  // Check for existing session
  const { data: { session }, error } = await supabase.auth.getSession();

  if (session) {
    // User is already signed in, redirect to dashboard
    window.location.href = CONFIG.redirectUrl;
    return;
  }

  // Handle OAuth callback
  if (window.location.hash.includes('access_token')) {
    const { data, error } = await supabase.auth.getSession();
    if (data.session) {
      window.location.href = CONFIG.redirectUrl;
      return;
    }
  }

  // Set up event listeners
  setupEventListeners();
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  // Sign in form
  if (signinForm) {
    signinForm.addEventListener('submit', handleSignIn);
  }

  // Sign up form
  if (signupForm) {
    signupForm.addEventListener('submit', handleSignUp);
  }

  // Social authentication buttons
  socialButtons.forEach(button => {
    button.addEventListener('click', () => handleSocialAuth(button.dataset.provider));
  });

  // Forgot password
  if (forgotPasswordLink) {
    forgotPasswordLink.addEventListener('click', handleForgotPassword);
  }

  // Listen for auth state changes
  supabase?.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session) {
      window.location.href = CONFIG.redirectUrl;
    }
  });
}

/**
 * Handle sign in form submission
 */
async function handleSignIn(event) {
  event.preventDefault();

  if (!supabase) {
    showMessage('Authentication service is not available.', 'error');
    return;
  }

  const form = event.target;
  const formData = new FormData(form);
  const email = formData.get('email').trim();
  const password = formData.get('password');

  if (!email || !password) {
    showMessage('Please fill in all required fields.', 'error');
    return;
  }

  setFormLoading(form, true);
  hideMessage();

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw error;
    }

    showMessage('Sign in successful! Redirecting...', 'success');

    // Redirect will be handled by auth state change listener

  } catch (error) {
    console.error('Sign in error:', error);
    let errorMessage = 'An error occurred during sign in.';

    if (error.message) {
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = 'Please confirm your email address before signing in.';
      } else {
        errorMessage = error.message;
      }
    }

    showMessage(errorMessage, 'error');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Handle sign up form submission
 */
async function handleSignUp(event) {
  event.preventDefault();

  if (!supabase) {
    showMessage('Authentication service is not available.', 'error');
    return;
  }

  const form = event.target;
  const formData = new FormData(form);
  const email = formData.get('email').trim();
  const password = formData.get('password');
  const displayName = formData.get('display')?.trim();
  const termsAccepted = formData.get('terms');

  if (!email || !password) {
    showMessage('Please fill in all required fields.', 'error');
    return;
  }

  if (password.length < 8) {
    showMessage('Password must be at least 8 characters long.', 'error');
    return;
  }

  if (!termsAccepted) {
    showMessage('Please accept the Terms of Service and Privacy Policy.', 'error');
    return;
  }

  setFormLoading(form, true);
  hideMessage();

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: displayName ? { display_name: displayName } : {},
        emailRedirectTo: CONFIG.redirectUrl
      }
    });

    if (error) {
      throw error;
    }

    if (data.user && !data.session) {
      // Email confirmation required
      showMessage(`Account created! Please check your email for a confirmation link.`, 'success');
      form.reset();
    } else if (data.session) {
      // User is automatically signed in
      showMessage('Account created successfully! Redirecting...', 'success');
    }

  } catch (error) {
    console.error('Sign up error:', error);
    let errorMessage = 'An error occurred during sign up.';

    if (error.message) {
      if (error.message.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.message.includes('Password should be')) {
        errorMessage = 'Password does not meet the requirements. Please choose a stronger password.';
      } else {
        errorMessage = error.message;
      }
    }

    showMessage(errorMessage, 'error');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Handle social authentication
 */
async function handleSocialAuth(provider) {
  if (!supabase) {
    showMessage('Authentication service is not available.', 'error');
    return;
  }

  hideMessage();

  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: CONFIG.redirectUrl
      }
    });

    if (error) {
      throw error;
    }

    // OAuth flow will redirect automatically

  } catch (error) {
    console.error('Social auth error:', error);
    showMessage(`Failed to sign in with ${provider}. Please try again.`, 'error');
  }
}

/**
 * Handle forgot password
 */
async function handleForgotPassword(event) {
  event.preventDefault();

  if (!supabase) {
    showMessage('Authentication service is not available.', 'error');
    return;
  }

  const email = prompt('Please enter your email address:');

  if (!email) {
    return;
  }

  if (!isValidEmail(email)) {
    showMessage('Please enter a valid email address.', 'error');
    return;
  }

  hideMessage();

  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password.html'
    });

    if (error) {
      throw error;
    }

    showMessage('Password reset link sent! Please check your email.', 'success');

  } catch (error) {
    console.error('Password reset error:', error);
    showMessage('Failed to send password reset link. Please try again.', 'error');
  }
}

/**
 * Set form loading state
 */
function setFormLoading(form, loading) {
  const submitButton = form.querySelector('button[type="submit"]');
  const buttonText = submitButton.querySelector('.btn-text');
  const spinner = submitButton.querySelector('.btn-spinner');

  if (loading) {
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    if (buttonText) buttonText.textContent = 'Processing...';
  } else {
    submitButton.disabled = false;
    submitButton.classList.remove('loading');

    // Restore original text based on form
    if (form.id === 'signin-form') {
      buttonText.textContent = 'Sign In';
    } else if (form.id === 'signup-form') {
      buttonText.textContent = 'Create Account';
    }
  }
}

/**
 * Show message to user
 */
function showMessage(text, variant = 'info') {
  if (!messageBox) return;

  messageBox.textContent = text;
  messageBox.dataset.variant = variant;
  messageBox.classList.add('is-visible');

  // Scroll message into view
  messageBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  // Auto-hide success messages after 5 seconds
  if (variant === 'success') {
    setTimeout(hideMessage, 5000);
  }
}

/**
 * Hide message
 */
function hideMessage() {
  if (!messageBox) return;

  messageBox.classList.remove('is-visible');
  messageBox.textContent = '';
  messageBox.removeAttribute('data-variant');
}

/**
 * Validate email format
 */
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Add error styling to form input
 */
function setFieldError(field, message) {
  field.classList.add('error');

  let errorElement = field.parentNode.querySelector('.form-error');
  if (!errorElement) {
    errorElement = document.createElement('div');
    errorElement.className = 'form-error';
    field.parentNode.appendChild(errorElement);
  }

  errorElement.textContent = message;
  errorElement.classList.add('is-visible');
}

/**
 * Clear error styling from form input
 */
function clearFieldError(field) {
  field.classList.remove('error');

  const errorElement = field.parentNode.querySelector('.form-error');
  if (errorElement) {
    errorElement.classList.remove('is-visible');
  }
}

/**
 * Initialize when DOM is ready
 */
document.addEventListener('DOMContentLoaded', initAuth);

// Export for use in other modules
export {
  supabase,
  showMessage,
  hideMessage,
  setFieldError,
  clearFieldError,
  isValidEmail
};